import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { toastManager } from "../components/ui/toast";

/**
 * The three clipboard copiers the thread action menu needs.
 *
 * Extracted when the chat breadcrumb became a second surface for that menu.
 * Copying a path, a branch and a thread id differ only in the noun they put in
 * the toast, and three near-identical `useCopyToClipboard` blocks per consumer
 * is how the two surfaces would have drifted — one growing a description the
 * other lacks, or reporting a failure the other swallows.
 *
 * @module useThreadClipboardCopiers
 */

function copyFailure(what: string) {
  return (error: unknown) => {
    toastManager.add({
      type: "error",
      title: `Failed to copy ${what}`,
      description: error instanceof Error ? error.message : "An error occurred.",
    });
  };
}

function copySuccess(title: string) {
  return (description: string) => {
    toastManager.add({ type: "success", title, description });
  };
}

export interface ThreadClipboardCopiers {
  readonly copyPathToClipboard: ReturnType<
    typeof useCopyToClipboard<{ path: string }>
  >["copyToClipboard"];
  readonly copyBranchToClipboard: ReturnType<
    typeof useCopyToClipboard<{ branch: string }>
  >["copyToClipboard"];
  readonly copyThreadIdToClipboard: ReturnType<
    typeof useCopyToClipboard<{ threadId: string }>
  >["copyToClipboard"];
}

export function useThreadClipboardCopiers(): ThreadClipboardCopiers {
  const { copyToClipboard: copyPathToClipboard } = useCopyToClipboard<{ path: string }>({
    onCopy: (context) => copySuccess("Path copied")(context.path),
    onError: copyFailure("path"),
  });
  const { copyToClipboard: copyBranchToClipboard } = useCopyToClipboard<{ branch: string }>({
    onCopy: (context) => copySuccess("Branch copied")(context.branch),
    onError: copyFailure("branch"),
  });
  const { copyToClipboard: copyThreadIdToClipboard } = useCopyToClipboard<{ threadId: string }>({
    onCopy: (context) => copySuccess("Thread ID copied")(context.threadId),
    onError: copyFailure("thread ID"),
  });

  return { copyPathToClipboard, copyBranchToClipboard, copyThreadIdToClipboard };
}
